'use client'
import { Teams } from '@glovo/organisation'
import debounce from 'lodash/debounce'
import Image from 'next/image'
import type { ChangeEvent, FC } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './FindAddressByText.module.scss'
import { useAuth } from '@/tools/auth'
import { useLocalisation } from '@/tools/i18n/client'
import { getImageUrl } from '@/tools/image-service/client'
import { useErrorHandling, withErrorBoundary } from '@/tools/o11y/client'
import {
  ArrowBackwardIcon,
  Button,
  ButtonSize,
  ButtonVariants,
  IconBlob,
  IconBlobType,
  IconButton,
  IconButtonSize,
  IconButtonVariants,
  LocationArrowIcon,
  SearchInput,
  SearchSize,
  useSnackbar,
} from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import { addressCheckEvent } from '@/user/address-book/analytics'
import type { GoogleAddressCompact, UserAddressNew } from '@/user/address-book/entities'
import { GeolocationPermission } from '@/user/address-book/types'
import { TitleDesktop } from '@/user/address-book/ui/components/title-desktop/TitleDesktop'
import { AddressEmptyState } from '@/user/address-book/ui/steps/find-address-by-text/address-empty-state/AddressEmptyState'
import { AddressSearchItem } from '@/user/address-book/ui/steps/find-address-by-text/address-search-item/AddressSearchItem'
import { ShareCurrentLocation } from '@/user/address-book/ui/steps/find-address-by-text/share-current-location/ShareCurrentLocation'
import { UseMapBlock } from '@/user/address-book/ui/steps/find-address-by-text/use-map-block/UseMapBlock'
import {
  getAddressByCoordinates,
  getAddressByPlaceId,
  isLocationInDeliveryArea,
  searchAddresses,
} from '@/user/address-book/use-cases'

const SEARCH_ADDRESS_DEBOUNCE = 600
const FOCUS_DELAY = 500
const MAX_RESULT = 5

export const FindAddressByText: FC = withErrorBoundary(
  () => {
    const { withErrorTracking, execWithErrorTracking } = useErrorHandling()
    const { t } = useLocalisation()
    const { showInfo } = useSnackbar(2500)

    const {
      navigate,
      browserLocation,
      setAddress,
      setIsInArea,
      setAddressFormTrigger,
      setOriginalCoordinates,
      isMobile,
      isLocationBeingResolved,
      requestLocation,
      verifiedBrowserAddress,
    } = useAddressBookContext()
    const { isLoggedIn } = useAuth()

    const [searchQuery, setSearchQuery] = useState('')
    const [searchTouched, setSearchTouched] = useState(false)
    const [resultAddresses, setResultAddresses] = useState<GoogleAddressCompact[]>([])
    const [clickedUseCurrentLocation, setClickedUseCurrentLocation] = useState(false)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const showEmptyState = !resultAddresses.length && searchTouched && Boolean(searchQuery)
    const showGoogleLogo = Boolean(resultAddresses.length) || showEmptyState
    const snackBarProps = {
      label: `${t('address-list.location-modal.title')}. ${t('address-list.location-modal.description')}.`,
      icon: <IconBlob icon={LocationArrowIcon} type={IconBlobType.critical} />,
    }
    const googleLogoSrc = getImageUrl({
      source: 'dh:customer-assets-glovo/address_flow/address_book/powered_by_google.svg',
    })

    const isUseCurrentLocationLoading = isLocationBeingResolved && clickedUseCurrentLocation

    const handleDesktopBackClick = isLoggedIn
      ? withErrorTracking(() => {
          if (isLoggedIn) {
            navigate('onBack')
          }
        })
      : undefined

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchResults = useCallback(
      debounce(async (query: string) => {
        if (!query) {
          setResultAddresses([])
          return
        }
        const addresses = await searchAddresses(query, isLoggedIn)
        setResultAddresses(addresses.slice(0, MAX_RESULT))
        if (!searchTouched) {
          setSearchTouched(true)
        }
      }, SEARCH_ADDRESS_DEBOUNCE),
      [setResultAddresses, searchTouched, setSearchTouched, isLoggedIn]
    )

    const handleSearchChange = withErrorTracking((e: ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value
      setSearchQuery(query)
      return fetchResults(query)
    })

    const handleSelectAddress = withErrorTracking(async (placeId: string) => {
      const address = await getAddressByPlaceId(placeId)
      const isInArea = await isLocationInDeliveryArea({
        latitude: address.latitude,
        longitude: address.longitude,
      })

      setIsInArea(isInArea)
      setAddress(address)
      if (isInArea) {
        onSetAddressSuccess(address)
      } else {
        if (isMobile) {
          navigate('onLocationIsOutOfZone')
        } else {
          showInfo(t('hyperlocal.outOfBounds'))
        }
        addressCheckEvent({
          address,
          addressStatusSuccessful: false,
          checkType: 'Search',
          query: searchQuery,
        })
      }
    })

    const onSetAddressSuccess = (address: UserAddressNew) => {
      setOriginalCoordinates(address)
      navigate('onLocationSelected')
      setAddressFormTrigger('Search')
      addressCheckEvent({
        address,
        addressStatusSuccessful: true,
        checkType: 'Search',
        query: searchQuery,
      })
    }

    const handleSubmit = withErrorTracking((event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      searchInputRef.current?.blur()
    })

    const onLocationAllowed = async (coordinates?: GeolocationCoordinates) => {
      if (!coordinates) {
        return
      }
      const isInDeliveryArea = await isLocationInDeliveryArea(coordinates)
      if (!isInDeliveryArea) {
        return null
      }
      const address = await getAddressByCoordinates(coordinates)
      if (address) {
        setIsInArea(true)
        setAddress(address)
        onSetAddressSuccess(address)
      }
    }

    const onLocationNotAllowed = () => {
      showInfo(snackBarProps.label, { icon: snackBarProps.icon })
    }

    const handleUseCurrentLocation = withErrorTracking(() => {
      setClickedUseCurrentLocation(true)
      if (
        browserLocation?.geolocationPermission === GeolocationPermission.DENIED ||
        browserLocation?.geolocationPermission === GeolocationPermission.UNSUPPORTED
      ) {
        showInfo(snackBarProps.label, { icon: snackBarProps.icon })
      } else if (
        browserLocation?.geolocationPermission === undefined ||
        browserLocation?.geolocationPermission === GeolocationPermission.PROMPT
      ) {
        requestLocation(onLocationAllowed, onLocationNotAllowed)
      } else if (
        browserLocation?.geolocationPermission === GeolocationPermission.GRANTED &&
        verifiedBrowserAddress
      ) {
        setIsInArea(true)
        setAddress(verifiedBrowserAddress)
        onSetAddressSuccess(verifiedBrowserAddress)
      }
    })

    const handleMobileBackClick = withErrorTracking(() => {
      navigate('onBack')
    })

    const handleUseMapInsteadClick = withErrorTracking(() => {
      navigate('useMapInstead')
    })

    useEffect(() => {
      let focusTimeout: NodeJS.Timeout
      execWithErrorTracking(() => {
        if (searchInputRef.current) {
          // Focus input after animation is over
          focusTimeout = setTimeout(() => searchInputRef.current?.focus(), FOCUS_DELAY)
        }
      })
      return withErrorTracking(() => {
        focusTimeout && clearTimeout(focusTimeout)
      })
    }, [searchInputRef, execWithErrorTracking, withErrorTracking])

    return (
      <div className={styles.findByText}>
        {!isMobile && (
          <TitleDesktop title={t('address-list.title')} onBackClick={handleDesktopBackClick} />
        )}
        <div className={styles.findByTextTop}>
          {isMobile && (
            <IconButton
              aria-label={t('glovo.common.back')}
              className={styles.backButton}
              icon={ArrowBackwardIcon}
              size={IconButtonSize.small}
              variant={IconButtonVariants.transparent}
              onClick={handleMobileBackClick}
            />
          )}
          <form className={styles.form} onSubmit={handleSubmit}>
            <SearchInput
              ref={searchInputRef}
              aria-label={t('common.search')}
              className={styles.searchInput}
              placeholder={t('address-list.search-address')}
              size={SearchSize.small}
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </form>
        </div>
        {isMobile &&
          browserLocation?.geolocationPermission !== GeolocationPermission.UNSUPPORTED && (
            <div className={styles.useCurrentLocation}>
              <ShareCurrentLocation onClick={handleUseCurrentLocation} />
            </div>
          )}

        <div className={styles.resultsList}>
          {resultAddresses.map((address, index) => (
            <AddressSearchItem
              key={index}
              address={address}
              isLastItem={index === resultAddresses.length - 1}
              onClick={handleSelectAddress}
            />
          ))}

          {showEmptyState && <AddressEmptyState searchQuery={searchQuery} />}

          {isMobile && searchTouched && (
            <div className={styles.useMapInstead}>
              <UseMapBlock onClick={handleUseMapInsteadClick} />
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {showGoogleLogo && (
            <Image alt="Google logo" height={18} src={googleLogoSrc} width={121} />
          )}

          {!isMobile && (
            <div className={styles.currentLocationButton}>
              <Button
                block
                disabled={
                  browserLocation?.geolocationPermission === GeolocationPermission.UNSUPPORTED
                }
                loading={isUseCurrentLocationLoading}
                size={ButtonSize.medium}
                type="button"
                variant={ButtonVariants.secondary}
                onClick={handleUseCurrentLocation}
              >
                {t('landing.address-container-location')}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  },
  {
    id: 'user.address_book.step.find_address_by_text',
    owner: Teams.WEBX,
  }
)
